// Got most of the source code from https://github.com/sstorie/experiments/blob/master/angular2-signalr/client/app/services/channel.service.ts
import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import { filter, takeUntil, throttleTime } from 'rxjs/operators';

import { ConnectionState, NotificationSubject, SignalrWindow, NotificationMessage } from './service-notification.interfaces';
import { ServerNotificationConfigService } from './server-notification-config.service';

declare var window: any;

@Injectable()
export class ServerNotificationService implements OnDestroy {

  starting$: Observable<any>;
  connectionState$: Observable<ConnectionState>;
  error$: Observable<string>;

  private srWindow: SignalrWindow = window;
  private connectionStateSubject = new Subject<ConnectionState>();
  private startingSubject = new Subject<any>();
  private errorSubject = new Subject<any>();

  private hubConnection: any;
  private hubProxy: any;

  private notificationSubject: NotificationSubject = undefined;
  private destroy$ = new Subject<boolean>();

  constructor(private config: ServerNotificationConfigService) {
    if (this.srWindow.$ === undefined || this.srWindow.$.hubConnection === undefined) {
      throw new Error('The variable \'$\' or the .hubConnection() function are not defined...please check the SignalR scripts have been loaded properly');
    }

    // Set up our observables
    this.connectionState$ = this.connectionStateSubject.asObservable();
    this.error$ = this.errorSubject.asObservable();
    this.starting$ = this.startingSubject.asObservable();

    this.hubConnection = this.srWindow.$.hubConnection();
    this.hubConnection.url = config.NotificationConfig.url + '/signalR';
    this.hubProxy = this.hubConnection.createHubProxy(config.NotificationConfig.hubName);

    this.handleHubStateChange();
    this.handleHubError();
    this.handleHubEvent();

    this.connectionState$
    .pipe(
      filter(connState => connState === ConnectionState.Disconnected),
      throttleTime(7000),
      takeUntil(this.destroy$)
    )
    .subscribe(() => {
      if (window['enablemclog']) {
        // console.log(`ServerNotification connection retry at ${(new Date())}`);
      }
      this.start();
    });

    this.hubConnection.disconnected(() => {
      setTimeout(() => {
        this.start();
      }, 5000);
   });
  }

  ngOnDestroy() {
    if (window['enablemclog']) {
      console.log('ServerNotificationService ngOnDestroy');
    }

    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  start(): void {
    this.hubConnection.start()
      .done(() => {
        this.startingSubject.next(true);
      })
      .fail((error: any) => {
        this.startingSubject.error(error);
      });
  }

  listen(groupName: string): Observable<NotificationMessage> {
    if (this.notificationSubject !== undefined && this.notificationSubject.groupName === groupName) {
      return this.notificationSubject.subject.asObservable();
    }

    if (this.notificationSubject !== undefined && this.notificationSubject.groupName !== groupName) {
      this.callServerMethod('UnRegister', this.notificationSubject.groupName);
    }

    this.notificationSubject = new NotificationSubject();
    this.notificationSubject.groupName = groupName;
    this.notificationSubject.subject = new Subject<NotificationMessage>();
    this.callServerMethod('Register', groupName);

    return this.notificationSubject.subject.asObservable();
  }

  private callServerMethod(methodName, parameter) {
    if (this.isHubConnected) {
      this.invokeHubMethod(methodName, parameter);
      return;
    }

    this.starting$.subscribe(() => {
      this.invokeHubMethod(methodName, parameter);
    },
      (error: any) => {
        this.notificationSubject.subject.error(error);
      });
  }

  private handleHubStateChange() {
    this.hubConnection.stateChanged((state: any) => {
      let newState = ConnectionState.Connecting;

      switch (state.newState) {
        case this.srWindow.$.signalR.connectionState.connecting:
          newState = ConnectionState.Connecting;
          break;
        case this.srWindow.$.signalR.connectionState.connected:
          newState = ConnectionState.Connected;
          break;
        case this.srWindow.$.signalR.connectionState.reconnecting:
          newState = ConnectionState.Reconnecting;
          break;
        case this.srWindow.$.signalR.connectionState.disconnected:
          newState = ConnectionState.Disconnected;
          break;
      }
      this.connectionStateSubject.next(newState);

      if (window['enablemclog']) {
        // console.log(`Hub state change to ${newState} at ${(new Date())}`);
      }
    });
  }

  private handleHubError() {
    this.hubConnection.error((error: any) => {
      this.errorSubject.next(error);
    });
  }

  private handleHubEvent() {
    this.hubProxy.on('onEvent', (groupName, ev: NotificationMessage) => {
      if (this.notificationSubject !== undefined) {
        return this.notificationSubject.subject.next(ev);
      }
    });
  }

  private invokeHubMethod(methodName, parameter) {
    this.hubProxy.invoke(methodName, parameter)
      .done(() => {
        // console.log(`Successfully invoke ${methodName} + ${parameter}`);
      })
      .fail((error: any) => {
        this.notificationSubject.subject.error(error);
      });
  }

  private get isHubConnected() {
    if (this.hubConnection && this.hubConnection.state) {
      return this.hubConnection.state === this.srWindow.$.signalR.connectionState.connected;
    }

    return false;
  }

  private get isHubDisconnected() {
    if (this.hubConnection && this.hubConnection.state) {
      return this.hubConnection.state === this.srWindow.$.signalR.connectionState.disconnected;
    }
    return false;
  }
}



// WEBPACK FOOTER //
// /home/runner/work/cdg-missioncontrol/cdg-missioncontrol/MissionControlClient2/MissionControl.WebUi2/mc-app/src/app/core/server-notification/server-notification.service.ts